import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of, merge, shareReplay } from 'rxjs';
import { switchMap, tap, catchError, map, take } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { SourceGroupApiService } from '../../../api/sourceGroups/source-group.api.service';
import { SourceGroupApi } from '../../../api/sourceGroups/models/source-group.api.model';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';

import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { SourceGroup } from '../models/source-group.model';
import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { ResourceAction } from '../../Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';


@Injectable()
export class SourceGroupService extends AllResourceAbstractService<SourceGroup, SourceGroupApi, SourceGroup, SourceGroupApi> {

    all$ = merge(
        this.refreshAllSubject.asObservable().pipe(
            switchMap(() => this.refreshAllManual())
        ),
        this.refreshAllManualSubject
    ).pipe(
        tap(sourceGroups => {
            this.mapSourceIdsToSourceGroups(sourceGroups);
        }),
        shareReplay(1)
    );

    private sourceToSourceGroupsMap: Map<number, SourceGroup[]>;
    private sourceToSourceGroupsMap$ = new BehaviorSubject<Map<number, SourceGroup[]>>(null);

    constructor(
        private sourceGroupApiService: SourceGroupApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {
        super();
    }

    getApiService(): MultiBaseAbstractApi<SourceGroupApi, SourceGroupApi> {
        return this.sourceGroupApiService;
    }

    constructResourceModel(apiModel: SourceGroupApi): SourceGroup {
        return new SourceGroup(apiModel);
    }

    constructListModel(apiModel: SourceGroupApi): SourceGroup {
        return new SourceGroup(apiModel);
    }

    create(sourceGroup: SourceGroup): Observable<number> {
        return this.createResource(sourceGroup).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    update(sourceGroup: SourceGroup): Observable<number> {
        return this.updateResource(sourceGroup, { actionType: ResourceActionTypes.UPDATE}).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    deleteBulk(sourceGroup: SourceGroup[]): Observable<void | number[]> {
        const action = {
            actionType: ResourceActionTypes.DELETE,
            count: sourceGroup.length
        };
        return this.bulkDeleteResource(sourceGroup, action).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    override bulkDeleteResource(
        resources: SourceGroup[],
        action: ResourceAction = {actionType: ResourceActionTypes.DELETE},
        broadcast: boolean = true
    ): Observable<number[]> {
        return this.getApiService().deleteByIds(resources.map(x => x.id)).pipe(
            tap(x => {
                if (broadcast) {
                    this.actionCompletedSubject.next(action);
                }
            }),
            catchError(err => {
                this.actionCompletedSubject.next({
                    ...action,
                    error: err
                });
                return of(null);
            })
        );
    }

    override getById(id: number): Observable<SourceGroup> {
        return this.all$.pipe(
            map((sourceGroups: SourceGroup[]) => {
                return sourceGroups.find(group => group.id === id);
            }),
            take(1)
        );
    };

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as SourceGroup;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'SourceGroup.createErrorHeading',
                        detail: this.localizationService.transform('SourceGroup.createErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'SourceGroup.createSucessHeading',
                        detail: this.localizationService.transform('SourceGroup.createSucessMessage', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    let detail = this.localizationService.transform('SourceGroup.updateErrorMessage', {
                        name: resource.name
                    });

                    if (error.code === CommonApiErrorEnum.duplicateName) {
                        detail = 'SourceGroup.error.duplicate';
                    }

                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('SourceGroup.updateErrorHeading'),
                        detail: detail
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'SourceGroup.updateSucessHeading',
                        detail: this.localizationService.transform('SourceGroup.updateSucessMessage', {
                            name: resource.name
                        })
                    });
                }
                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'SourceGroup.deleteErrorHeading',
                        detail: this.localizationService.transform('SourceGroup.deleteErrorMessage', { count: actionResult.count })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'SourceGroup.deleteSucessHeading',
                        detail: this.localizationService.transform('SourceGroup.deleteSucessMessage', { count: actionResult.count })
                    });
                }
                break;
            }
        }
    }


    private mapSourceIdsToSourceGroups(sourceGroups: SourceGroup[]) {
        if (!sourceGroups) {

            return;
        }

        this.sourceToSourceGroupsMap = new Map<number, SourceGroup[]>();
        sourceGroups.forEach(sourceGroup => {
            if (!sourceGroup.sourceIds) {
                return;
            }
            sourceGroup.sourceIds.forEach(sourceId => {
                let sourceGroupsWithSource = this.sourceToSourceGroupsMap.get(sourceId);
                if (!sourceGroupsWithSource) {
                    this.sourceToSourceGroupsMap.set(sourceId, []);
                    sourceGroupsWithSource = this.sourceToSourceGroupsMap.get(sourceId);
                }
                sourceGroupsWithSource.push(sourceGroup);
                this.sourceToSourceGroupsMap.set(sourceId, sourceGroupsWithSource);
            });
        });
        this.sourceToSourceGroupsMap$.next(this.sourceToSourceGroupsMap);
    }

    getSourceToSourceGroupsMap(): Observable<Map<number, SourceGroup[]>> {
        return this.sourceToSourceGroupsMap$;
    }
}
